import { useState, useEffect, useRef } from "react";
import emailjs from '@emailjs/browser';

const SLIDES = [
  null,
  "/boxes.jpeg",
  "/server_inside_2.jpeg",
  "/server_inside_3.jpeg",
  "/server_full.jpeg",
  "/telecom_rack.jpeg",
  "/telecom_rack_inside.jpeg",
  "/cables.jpeg",
  "/cables_2.jpeg",
  "/access_point.jpeg",
];
const SLIDE_COUNT = SLIDES.length;

const NAV_LINKS = [
  { key: "navDirections", href: "#directions" },
  { key: "navPartners", href: "#partners" },
  { key: "navCerts", href: "#certs" },
  { key: "navContact", href: "#contact" },
];


const CLIENTS = [
  "Invictus Semey",
  "RIXOS Khan Shatyr Residences",
  "ТОО «ЖОЛҚҰРЫЛЫС-НС Астана»",
  "Кирпичный завод ТОО «AB-SAL»",
  "Медицинский Колледж «ТОМИРИС»",
  "Высший Колледж Кайнар, г. Семей",
  "НАО Шәкәрім Университет, г. Семей",
];

const VENDORS = [
  "Cisco", "Dell", "Huawei", "Hikvision",
  "Microsoft", "HP", "Veeam",
  "Kaspersky", "Cloudflare", "Fortinet", "Palo Alto Networks",
  "APC by Schneider Electric",
];

const CERTS_CISCO = [
  { code: "CISCO CCNP Security:2023", desc: "Network Security Firepower" },
  { code: "CISCO CCNP Security:2023", desc: "Security Core" },
  { code: "CISCO CCNA:2023", desc: "Network Associate" },
];

const CERTS_HUAWEI = [
  { code: "HUAWEI HCSA Presales:2025", desc: "Associate Presales-IP Network" },
  { code: "HUAWEI HCSA Presales:2025", desc: "Associate Presales-Storage" },
];

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
}

function AnimSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const TEXT = {
  RU: {
    navDirections: "Направления",
    navPartners: "Партнёры",
    navCerts: "Сертификаты",
    navContact: "Контакты",

    subtitle: "IT-оборудование · Интеграция · Под ключ",
    country: "Казахстан · ТОО «KazTechNet»",

    heroLines: [
      "Поставка IT-оборудования",
      "Системная интеграция под ключ",
      "Проекты по всему Казахстану",
    ],

    heroFeatures: [
      "Серверное оборудование и системы хранения данных",
      "Сетевая инфраструктура и кибербезопасность",
      "Комплексная поставка и интеграция под ключ",
      "Системы ВКС (видеоконференцсвязь)",
    ],

    call: "Позвонить",
    whatsapp: "Написать в WhatsApp",

    stats: [
      { n: "15+", label: "Реализованных проектов" },
      { n: "12+", label: "Вендоров и брендов" },
      { n: "5", label: "Post & Pre-sale сертификаты" },
    ],

    about: "О компании",
    aboutTitle: "Поставка IT-оборудования под ключ",

    aboutText1:
      "ТОО «KazTechNet» осуществляет комплексную поставку IT-оборудования для государственных организаций, образовательных учреждений и коммерческих компаний.",

    aboutText2:
      "Мы предлагаем современные решения для построения надёжной, безопасной и масштабируемой IT-инфраструктуры.",

    aboutText3:
      "Мы не просто поставляем оборудование — мы создаём готовые IT-решения: от проектирования инфраструктуры до внедрения, настройки и технического сопровождения.",

    suppliedTitle: "Что мы поставляем",

    suppliedItems: [
      "Серверное оборудование",
      "Системы хранения данных (СХД)",
      "Сетевое оборудование",
      "Wi-Fi и телеком-решения",
      "Межсетевые экраны и системы кибербезопасности",
      "Интерактивные панели и решения для ВКС",
      "Рабочие станции, моноблоки и ноутбуки",
      "Источники бесперебойного питания (UPS)",
      "Системы видеонаблюдения и СКУД",
    ],

    directions: "Основные направления",
    whatWeDo: "Что мы делаем",

    services: [
      {
        tag: "СЕРВЕРЫ / СХД",
        title: "Серверное оборудование и системы хранения данных",
        desc: "Серверы, системы хранения данных, рабочие станции, моноблоки и ноутбуки от ведущих мировых производителей.",
        icon: "🖥️",
        accent: "#22c55e",
        id: 1,
      },

      {
        tag: "СЕТЬ / БЕЗОПАСНОСТЬ",
        title: "Сетевое оборудование и кибербезопасность",
        desc: "Коммутаторы, Wi-Fi и телеком-решения, межсетевые экраны, системы кибербезопасности и UPS.",
        icon: "🔒",
        accent: "#3b82f6",
        id: 2,
      },

      {
        tag: "ИНТЕГРАЦИЯ / ПОД КЛЮЧ",
        title: "Комплексная IT-интеграция",
        desc: "Проектирование инфраструктуры, внедрение, настройка и техническое сопровождение. ВКС, СКУД, видеонаблюдение.",
        icon: "⚙️",
        accent: "#f59e0b",
        id: 3,
      },
    ],

    advantagesTitle: "Наши преимущества",

    advantages: [
      "Официальные поставки от ведущих мировых производителей",
      "Подбор оборудования под задачи и бюджет заказчика",
      "Подготовка технической спецификации и сопровождение проектов",
      "Гарантийная и постгарантийная поддержка",
      "Поставка оборудования «под ключ»",
      "Опыт работы с государственными закупками и корпоративным сектором",
    ],

    technologies: "Технологии",
    technologiesTitle: "Партнёрство и технологии",

    technologiesDesc:
      "Мы работаем с ведущими мировыми брендами и предлагаем современные решения в следующих направлениях:",

    technologiesItems: [
      "Сетевая инфраструктура",
      "Хранение и обработка данных",
      "Информационная безопасность",
      "Цифровизация офисов и учебных заведений",
      "Монтаж структурированных кабельных систем",
    ],

    send: "Отправить заявку",
    sending: "Отправка...",
    partners: "Партнёры",
    clientsTitle: "Наши клиенты",
    vendorsTitle: "Вендоры",
    quality: "Качество",
    certificates: "Сертификаты",
    contactTitle: "Свяжитесь с нами",
    contactHeading: "Готовы обсудить ваш проект?",
    contactText:
      "ТОО «KazTechNet» — надёжный партнёр в сфере системной интеграции и поставки IT-оборудования. Оставьте заявку — ответим в течение рабочего дня.",
    formName: "Имя",
    formNamePlaceholder: "Ваше имя",
    formEmail: "Email",
    formPhone: "Телефон",
    formCompany: "Компания",
    formCompanyPlaceholder: "Название компании",
    formMessage: "Сообщение",
    formMessagePlaceholder: "Опишите ваш проект...",
    footerRights: "© ТОО «KazTechNet». Все права защищены.",
    location: "Казахстан",
    projects: "Проектов",
  },

  EN: {
    navDirections: "Services",
    navPartners: "Partners",
    navCerts: "Certificates",
    navContact: "Contacts",

    subtitle: "IT equipment · Integration · Turnkey",
    country: "Kazakhstan · KazTechNet LLP",

    heroLines: [
      "IT equipment supply",
      "Turnkey system integration",
      "Projects across Kazakhstan",
    ],

    heroFeatures: [
      "Server equipment and storage systems",
      "Network infrastructure and cybersecurity",
      "Turnkey supply and integration",
      "Video conferencing systems (VCS)",
    ],

    call: "Call",
    whatsapp: "Message on WhatsApp",

    stats: [
      { n: "15+", label: "Completed projects" },
      { n: "12+", label: "Vendors and brands" },
      { n: "5", label: "Post & Pre-sale certificates" },
    ],

    about: "About company",
    aboutTitle: "Turnkey IT equipment supply",

    aboutText1:
      "KazTechNet LLP provides comprehensive IT equipment supply for government organizations, educational institutions, and commercial companies.",

    aboutText2:
      "We offer modern solutions for building reliable, secure, and scalable IT infrastructure.",

    aboutText3:
      "We do not just supply equipment — we create complete IT solutions: from infrastructure design to implementation, configuration, and technical support.",

    suppliedTitle: "What we supply",

    suppliedItems: [
      "Server equipment",
      "Storage systems",
      "Network equipment",
      "Wi-Fi and telecom solutions",
      "Firewalls and cybersecurity systems",
      "Interactive panels and VCS solutions",
      "Workstations, all-in-one PCs, and laptops",
      "Uninterruptible power supplies (UPS)",
      "Video surveillance and access control systems",
    ],

    directions: "Main directions",
    whatWeDo: "What we do",

    services: [
      {
        tag: "SERVERS / STORAGE",
        title: "Server equipment and storage systems",
        desc: "Servers, storage systems, workstations, all-in-one PCs, and laptops from leading global manufacturers.",
      },

      {
        tag: "NETWORK / SECURITY",
        title: "Network equipment and cybersecurity",
        desc: "Switches, Wi-Fi and telecom solutions, firewalls, cybersecurity systems, and UPS.",
      },

      {
        tag: "INTEGRATION / TURNKEY",
        title: "Comprehensive IT integration",
        desc: "From infrastructure design to deployment, configuration, and technical support. Interactive panels, VCS, access control, and video surveillance.",
      },
    ],

    advantagesTitle: "Our advantages",

    advantages: [
      "Official supplies from leading global manufacturers",
      "Equipment selection according to customer needs and budget",
      "Technical specification preparation and project support",
      "Warranty and post-warranty support",
      "Turnkey equipment supply",
      "Experience with government procurement and corporate sector",
    ],

    technologies: "Technologies",
    technologiesTitle: "Partnerships and technologies",

    technologiesDesc:
      "We work with leading global brands and offer modern solutions in the following areas:",

    technologiesItems: [
      "Network infrastructure",
      "Data storage and processing",
      "Information security",
      "Digitalization of offices and educational institutions",
      "Structured cabling systems installation",
    ],

    send: "Submit request",
    sending: "Sending...",

    partners: "Partners",
    clientsTitle: "Our clients",
    vendorsTitle: "Vendors",
    quality: "Quality",
    certificates: "Certificates",
    contactTitle: "Contact us",
    contactHeading: "Ready to discuss your project?",
    contactText:
      "KazTechNet LLP is a reliable partner in system integration and IT equipment supply. Submit a request — we will respond within one business day.",
    formName: "Name",
    formNamePlaceholder: "Name",
    formEmail: "Email",
    formPhone: "Phone",
    formCompany: "Company",
    formCompanyPlaceholder: "Company name",
    formMessage: "Message",
    formMessagePlaceholder: "Describe your project...",
    footerRights: "© KazTechNet LLP. All rights reserved.",
    location: "Kazakhstan",
    projects: "Projects",
  },
};

export default function KazTechNet() {
  const [lang, setLang] = useState("RU");
  const t = TEXT[lang];
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState(null); // null | "sending" | "success" | "error"

  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Введите имя";
    if (!formData.phone.trim()) errors.phone = "Введите телефон";
    else if (!/^\+\d{7,15}$/.test(formData.phone.trim())) errors.phone = "Формат: +77001234567";
    if (!formData.company.trim()) errors.company = "Введите компанию";
    if (!formData.email.trim()) errors.email = "Введите email";
    else if (!formData.email.includes("@")) errors.email = "Email должен содержать @";
    if (!formData.message.trim()) errors.message = "Опишите проект";
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setFormStatus("sending");
    try {
      await emailjs.send(
        "service_e33caiu",   
        "template_9p3oxrh",
        {
          from_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          to_email: "service@telecommunity.kz",
        },
        "cXbB-DPi73-stEo7Z"    
      );
      setFormStatus("success");
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      setFormStatus("error");
    }
  };

  const heroLinesCount = t.heroLines.length;
  useEffect(() => {
    const t = setInterval(() => setHeroIndex(i => (i + 1) % heroLinesCount), 3500);
    return () => clearInterval(t);
  }, [heroLinesCount]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#0b0f17", color: "#f0ede8", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0b0f17; }
        ::-webkit-scrollbar-thumb { background: #22c55e; border-radius: 2px; }
        a { color: inherit; text-decoration: none; }
        .nav-link { position: relative; font-size: 14px; color: rgba(240,237,232,0.7); transition: color 0.2s; letter-spacing: 0.04em; }
        .nav-link::after { content: ''; position: absolute; bottom: -3px; left: 0; width: 0; height: 1px; background: #22c55e; transition: width 0.3s; }
        .nav-link:hover { color: #f0ede8; }
        .nav-link:hover::after { width: 100%; }
        .btn-primary { background: #22c55e; color: #0b0f17; border: none; padding: 13px 28px; border-radius: 100px; font-size: 14px; font-weight: 600; cursor: pointer; letter-spacing: 0.03em; transition: background 0.2s, transform 0.15s; }
        .btn-primary:hover { background: #16a34a; transform: translateY(-1px); }
        .btn-outline { background: transparent; color: #f0ede8; border: 1px solid rgba(240,237,232,0.3); padding: 13px 28px; border-radius: 100px; font-size: 14px; font-weight: 500; cursor: pointer; letter-spacing: 0.03em; transition: border-color 0.2s, transform 0.15s, background 0.2s; }
        .btn-outline:hover { border-color: #f0ede8; transform: translateY(-1px); background: rgba(240,237,232,0.05); }
        .service-card { border: 1px solid rgba(255,255,255,0.07); border-radius: 20px; padding: 36px; cursor: pointer; transition: border-color 0.3s, transform 0.3s, background 0.3s; background: rgba(255,255,255,0.02); position: relative; overflow: hidden; }
        .service-card:hover { border-color: rgba(34,197,94,0.4); transform: translateY(-6px); background: rgba(34,197,94,0.04); }
        .service-card::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 80% 20%, rgba(34,197,94,0.05) 0%, transparent 60%); opacity: 0; transition: opacity 0.4s; }
        .service-card:hover::before { opacity: 1; }
        .ticker { display: flex; gap: 48px; animation: ticker 22s linear infinite; white-space: nowrap; }
        @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .tag-pill { display: inline-block; font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; padding: 4px 12px; border-radius: 100px; border: 1px solid; margin-bottom: 16px; }
        .cert-card { border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 32px 28px; text-align: center; transition: border-color 0.3s, transform 0.3s; }
        .cert-card:hover { border-color: rgba(34,197,94,0.5); transform: translateY(-4px); }
        .stat-num { font-family: 'Playfair Display', serif; font-size: 52px; font-weight: 700; line-height: 1; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        .live-dot { width:8px; height:8px; border-radius:50%; background:#22c55e; animation: pulse-dot 2s infinite; display:inline-block; margin-right:8px; }
        .adv-card { border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; padding: 22px 24px; display: flex; align-items: flex-start; gap: 14px; transition: border-color 0.2s, background 0.2s; }
        .adv-card:hover { border-color: rgba(34,197,94,0.35); background: rgba(34,197,94,0.03); }
        .mobile-menu { position: fixed; top: 68px; left: 0; right: 0; bottom: 0; background: rgba(11,15,23,0.98); backdrop-filter: blur(20px); z-index: 99; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 32px; }
        @media (max-width: 768px) {
          .btn-primary, .btn-outline { padding: 11px 20px; font-size: 13px; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: isMobile ? "0 20px" : "0 48px",
        height: 68,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrolled || menuOpen ? "rgba(11,15,23,0.97)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.05)" : "1px solid transparent",
        transition: "all 0.4s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
            <rect x="4" y="4" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="22" y="4" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="4" y="22" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="22" y="22" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
          </svg>
          <div>
            <div style={{ fontWeight: 700, fontSize: isMobile ? 18 : 24, letterSpacing: "0.08em" }}>KazTechNet</div>
            {!isMobile && <div style={{ fontSize: 12, color: "rgba(240,237,232,0.5)", letterSpacing: "0.1em" }}>{t.subtitle}</div>}
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {["RU", "EN"].map(l => (
                <button key={l} onClick={() => setLang(l)} style={{
                  background: lang === l ? "#22c55e" : "transparent",
                  color: lang === l ? "#0b0f17" : "rgba(240,237,232,0.5)",
                  border: "1px solid", borderColor: lang === l ? "#22c55e" : "rgba(240,237,232,0.2)",
                  borderRadius: "100px", padding: "3px 10px", fontSize: 11, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.2s",
                }}>{l}</button>
              ))}
            </div>
            <button onClick={() => setMenuOpen(o => !o)} style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "#f0ede8", padding: 4, display: "flex", flexDirection: "column", gap: 5,
            }}>
              {menuOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              )}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 36 }}>
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} className="nav-link">{t[l.key]}</a>
            ))}
            <div style={{ display: "flex", gap: 4, marginLeft: 16 }}>
              {["RU", "EN"].map(l => (
                <button key={l} onClick={() => setLang(l)} style={{
                  background: lang === l ? "#22c55e" : "transparent",
                  color: lang === l ? "#0b0f17" : "rgba(240,237,232,0.5)",
                  border: "1px solid", borderColor: lang === l ? "#22c55e" : "rgba(240,237,232,0.2)",
                  borderRadius: "100px", padding: "4px 14px", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", letterSpacing: "0.06em", transition: "all 0.2s",
                }}>{l}</button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* MOBILE MENU */}
      {isMobile && menuOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
              style={{ fontSize: 22, fontWeight: 600, color: "rgba(240,237,232,0.85)", letterSpacing: "0.04em", transition: "color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.color = "#22c55e"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(240,237,232,0.85)"}
            >{t[l.key]}</a>
          ))}
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <a href="tel:+77026729498"><button className="btn-outline">{t.call}</button></a>
            <a href="https://wa.me/77026729498" target="_blank" rel="noreferrer"><button className="btn-primary">WhatsApp</button></a>
          </div>
        </div>
      )}

      {/* HERO */}
      <section style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "100px 20px 60px" : "120px 48px 80px",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }} />
        <div style={{
          position: "absolute", top: "20%", left: "55%",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(34,197,94,0.10) 0%, transparent 70%)",
          zIndex: 0, pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", top: "60%", left: "20%",
          width: 400, height: 400,
          background: "radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 70%)",
          zIndex: 0, pointerEvents: "none",
        }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, width: "100%", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
              <span className="live-dot" />
              <span style={{ fontSize: 12, color: "rgba(240,237,232,0.5)", letterSpacing: "0.1em", textTransform: "uppercase" }}>{t.country}</span>
            </div>

            <div style={{ minHeight: isMobile ? 120 : 180, marginBottom: 32, position: "relative" }}>
              {t.heroLines.map((line, i) => (
                <h1
                  key={i}
                  className="hero-title"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: isMobile ? 36 : 58,
                    fontWeight: 700,
                    lineHeight: 1.12,
                    position: i === 0 ? "relative" : "absolute",
                    top: i === 0 ? "auto" : 0,
                    opacity: heroIndex === i ? 1 : 0,
                    transform: heroIndex === i ? "translateY(0)" : "translateY(20px)",
                    transition: "opacity 0.6s, transform 0.6s",
                    pointerEvents: "none",
                  }}
                >
                  {line}
                </h1>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 44 }}>
              {t.heroFeatures.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
                  <span style={{ fontSize: 15, color: "rgba(240,237,232,0.75)" }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 36 }}>
              <a href="tel:+77026729498"><button className="btn-outline">{t.call}</button></a>
              <a href="https://wa.me/77026729498" target="_blank" rel="noreferrer"><button className="btn-primary">{t.whatsapp}</button></a>
              <a href="https://t.me/KazTechNet_bot" target="_blank" rel="noreferrer">
                <button className="btn-outline" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.48 14.373l-2.95-.924c-.64-.203-.654-.64.136-.954l11.5-4.433c.537-.194 1.006.131.396.186z"/>
                  </svg>
                  Telegram
                </button>
              </a>
            </div>

            <div style={{ display: "flex", gap: 24 }}>
              <a href="tel:+77026729498" style={{ fontSize: 13, color: "rgba(240,237,232,0.45)", letterSpacing: "0.02em" }}>+7 (702) 672-94-98</a>
              <a href="mailto:service@telecommunity.kz" style={{ fontSize: 13, color: "rgba(240,237,232,0.45)" }}>service@telecommunity.kz</a>
            </div>
          </div>

          {/* Hero visual */}
          <div style={{ position: "relative" }}>
            <div style={{
              border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: 28,
              overflow: "hidden",
              background: "rgba(255,255,255,0.03)",
              aspectRatio: "4/3",
              position: "relative",
            }}>
              {SLIDES.map((src, i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: slideIndex === i ? 1 : 0,
                    transition: "opacity 0.7s ease",
                  }}
                >
                  {src === null ? (
                    <div style={{
                      width: "100%", height: "100%",
                      background: "linear-gradient(135deg, #0f1a0f 0%, #111827 50%, #0b1520 100%)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg viewBox="0 0 400 300" style={{ width: "85%", opacity: 0.9 }}>
                        <rect x="120" y="40" width="160" height="200" rx="8" fill="#1a2030" stroke="rgba(34,197,94,0.3)" strokeWidth="1"/>
                        {[0,1,2,3,4,5].map(j => (
                          <g key={j}>
                            <rect x="130" y={55 + j * 30} width="140" height="20" rx="3" fill="#111827" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
                            <circle cx="145" cy={65 + j * 30} r="4" fill={j % 3 === 0 ? "#22c55e" : j % 3 === 1 ? "#3b82f6" : "#f59e0b"}>
                              <animate attributeName="opacity" values="1;0.4;1" dur={`${1.5 + j * 0.3}s`} repeatCount="indefinite"/>
                            </circle>
                            <rect x="155" y={61 + j * 30} width="60" height="3" rx="1" fill="rgba(255,255,255,0.08)"/>
                            <rect x="155" y={67 + j * 30} width="40" height="3" rx="1" fill="rgba(255,255,255,0.05)"/>
                            <rect x="240" y={60 + j * 30} width="20" height="10" rx="2" fill="#0f172a" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                          </g>
                        ))}
                        <line x1="280" y1="80" x2="340" y2="60" stroke="rgba(34,197,94,0.3)" strokeWidth="1" strokeDasharray="4 3"/>
                        <line x1="280" y1="110" x2="340" y2="120" stroke="rgba(59,130,246,0.3)" strokeWidth="1" strokeDasharray="4 3"/>
                        <line x1="280" y1="140" x2="340" y2="180" stroke="rgba(34,197,94,0.2)" strokeWidth="1" strokeDasharray="4 3"/>
                        {[[340,60,"#22c55e"],[340,120,"#3b82f6"],[340,180,"#22c55e"]].map(([x,y,c],j) => (
                          <circle key={j} cx={x} cy={y} r="8" fill={c} opacity="0.15" stroke={c} strokeWidth="1.5">
                            <animate attributeName="r" values="8;11;8" dur={`${2+j*0.5}s`} repeatCount="indefinite"/>
                          </circle>
                        ))}
                        <rect x="20" y="100" width="85" height="65" rx="6" fill="rgba(34,197,94,0.06)" stroke="rgba(34,197,94,0.25)" strokeWidth="1"/>
                        <text x="62" y="118" textAnchor="middle" fill="#22c55e" fontSize="7" fontFamily="monospace">СТАТУС</text>
                        <text x="62" y="132" textAnchor="middle" fill="rgba(34,197,94,0.8)" fontSize="8" fontFamily="monospace">CPU: 34%</text>
                        <text x="62" y="146" textAnchor="middle" fill="rgba(34,197,94,0.6)" fontSize="8" fontFamily="monospace">RAM: 61%</text>
                        <text x="62" y="160" textAnchor="middle" fill="rgba(34,197,94,0.5)" fontSize="7" fontFamily="monospace">OK ✓</text>
                      </svg>
                    </div>
                  ) : (
                    <img
                      src={src}
                      alt={`Проект ${i}`}
                      style={{ width: "70%", height: "100%", objectFit: "cover", objectPosition: "center", transform: "scale(0.80)", transformOrigin: "center" }}
                    />
                  )}
                </div>
              ))}
              {/* Затемнение снизу */}
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(to top, rgba(11,15,23,0.6) 0%, transparent 50%)",
                zIndex: 1,
              }} />

              {/* Кнопки назад / вперёд */}
              <button
                onClick={() => setSlideIndex(i => (i - 1 + SLIDE_COUNT) % SLIDE_COUNT)}
                style={{
                  position: "absolute", left: 14, top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  background: "rgba(11,15,23,0.7)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 38, height: 38,
                  color: "#f0ede8", fontSize: 16,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                  backdropFilter: "blur(8px)",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(34,197,94,0.25)"; e.currentTarget.style.borderColor = "#22c55e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(11,15,23,0.7)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
              >‹</button>

              <button
                onClick={() => setSlideIndex(i => (i + 1) % SLIDE_COUNT)}
                style={{
                  position: "absolute", right: 14, top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  background: "rgba(11,15,23,0.7)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 38, height: 38,
                  color: "#f0ede8", fontSize: 16,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                  backdropFilter: "blur(8px)",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(34,197,94,0.25)"; e.currentTarget.style.borderColor = "#22c55e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(11,15,23,0.7)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
              >›</button>

              {/* Dots */}
              <div style={{
                position: "absolute", bottom: 14, left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2,
                display: "flex", gap: 6,
              }}>
                {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setSlideIndex(i)}
                    style={{
                      width: slideIndex === i ? 20 : 6,
                      height: 6,
                      borderRadius: 3,
                      background: slideIndex === i ? "#22c55e" : "rgba(255,255,255,0.35)",
                      cursor: "pointer",
                      transition: "width 0.3s, background 0.3s",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Floating badge */}
            <div style={{
              position: "absolute", bottom: -16, left: 28,
              background: "rgba(11,15,23,0.9)",
              border: "1px solid rgba(34,197,94,0.3)",
              borderRadius: 12,
              padding: "10px 18px",
              backdropFilter: "blur(10px)",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span className="live-dot" />
              <span style={{ fontSize: 12, fontWeight: 500 }}>{t.projects}</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#22c55e" }}>15+</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <AnimSection>
        <div style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: isMobile ? "36px 20px" : "52px 48px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(4, 1fr)", gap: 32, textAlign: "center" }}>
            {t.stats.map((s, i) => (
              <div key={i} style={{ padding: "12px 0" }}>
                <div className="stat-num" style={{ color: "#22c55e" }}>{s.n}</div>
                <div style={{ fontSize: 13, color: "rgba(240,237,232,0.5)", marginTop: 8, letterSpacing: "0.04em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </AnimSection>

      {/* ABOUT */}
      <section style={{ padding: isMobile ? "60px 20px" : "100px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.about}</div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 38, fontWeight: 700, lineHeight: 1.25, marginBottom: 24 }}>
                  {t.aboutTitle}
                </h2>
                <p style={{ fontSize: 15, color: "rgba(240,237,232,0.65)", lineHeight: 1.8, marginBottom: 20 }}>
                  {t.aboutText1}
                </p>
                <p style={{ fontSize: 15, color: "rgba(240,237,232,0.65)", lineHeight: 1.8, marginBottom: 20 }}>
                  {t.aboutText2}
                </p>
                <p style={{ fontSize: 15, color: "rgba(240,237,232,0.65)", lineHeight: 1.8 }}>
                  {t.aboutText3}
                </p>
              </div>
              <div>
                <div style={{ fontSize: 13, color: "rgba(240,237,232,0.45)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20 }}>{t.suppliedTitle}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                 {t.suppliedItems.map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
                      </div>
                      <span style={{ fontSize: 14, color: "rgba(240,237,232,0.75)", lineHeight: 1.5 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* DIRECTIONS */}
      <section id="directions" style={{ padding: isMobile ? "0 20px 60px" : "0 48px 100px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{ marginBottom: 64 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.directions}</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700, lineHeight: 1.2 }}>
                {t.whatWeDo}
              </h2>
            </div>
          </AnimSection>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 20 }}>
            {t.services.map((s, i) => (
              <AnimSection key={s.id} delay={i * 100}>
                <div
                  className="service-card"
                  onClick={() => setActiveService(activeService === s.id ? null : s.id)}
                  style={{ height: "100%" }}
                >
                  <span className="tag-pill" style={{ color: s.accent, borderColor: `${s.accent}40` }}>{s.tag}</span>
                  <div style={{ fontSize: 32, marginBottom: 16 }}>{s.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.35, marginBottom: 14 }}>{s.title}</h3>
                  <p style={{ fontSize: 14, color: "rgba(240,237,232,0.55)", lineHeight: 1.7 }}>{s.desc}</p>
                  <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: s.accent }}>
                    {/*<span>Подробнее</span>
                    <span style={{ fontSize: 16, transform: activeService === s.id ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.3s" }}>→</span> */}
                  </div>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section style={{ padding: isMobile ? "0 20px 60px" : "0 48px 100px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{ marginBottom: 52 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>Почему мы</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700 }}>{t.advantagesTitle}</h2>
            </div>
          </AnimSection>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14 }}>
            {t.advantages.map((adv, i) => (
              <AnimSection key={i} delay={i * 60}>
                <div className="adv-card">
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14, color: "#22c55e", fontWeight: 700 }}>
                    {i + 1}
                  </div>
                  <span style={{ fontSize: 14, color: "rgba(240,237,232,0.75)", lineHeight: 1.6 }}>{adv}</span>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP AREAS */}
      <section style={{ padding: isMobile ? "0 20px 60px" : "0 48px 100px", background: "rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingTop: 80 }}>
          <AnimSection>
            <div style={{ marginBottom: 52, textAlign: "center" }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.technologies}</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700 }}>{t.technologiesTitle}</h2>
              <p
                style={{
                  fontSize: 15,
                  color: "rgba(240,237,232,0.55)",
                  marginTop: 16,
                  maxWidth: 700,
                  lineHeight: 1.7,
                  textAlign: "center",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {t.technologiesDesc}
              </p>
            </div>
          </AnimSection>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 16 }}>
            {[
              { icon: "🌐", title: t.technologiesItems[0] },
              { icon: "💾", title: t.technologiesItems[1] },
              { icon: "🔐", title: t.technologiesItems[2] },
              { icon: "🏢", title: t.technologiesItems[3] },
              { icon: "🔌", title: t.technologiesItems[4] },
            ].map((item, i) => (
              <AnimSection key={i} delay={i * 80}>
                <div style={{
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 16,
                  padding: "28px 20px",
                  textAlign: "center",
                  transition: "border-color 0.3s, transform 0.3s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(34,197,94,0.4)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ fontSize: 28, marginBottom: 14 }}>{item.icon}</div>
                  <div style={{ fontSize: 13, color: "rgba(240,237,232,0.65)", lineHeight: 1.5 }}>{item.title}</div>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div style={{
        padding: "28px 0",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        overflow: "hidden",
        background: "rgba(34,197,94,0.03)",
      }}>
        <div className="ticker">
          {[...VENDORS, ...VENDORS].map((v, i) => (
            <span key={i} style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(240,237,232,0.4)" }}>
              {v} <span style={{ color: "#22c55e", marginLeft: 24 }}>·</span>
            </span>
          ))}
        </div>
      </div>

      {/* PARTNERS */}
      <section id="partners" style={{ padding: isMobile ? "60px 20px" : "100px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{ marginBottom: 56 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.partners}</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700 }}>{t.clientsTitle}</h2>
            </div>
          </AnimSection>

          <AnimSection delay={100}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 64, justifyContent: "center", }}>
              {CLIENTS.map((c, i) => (
                <div key={i} style={{
                  padding: "12px 24px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 100,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "rgba(240,237,232,0.7)",
                  cursor: "default",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  letterSpacing: "0.02em",
                  background: "rgba(255,255,255,0.02)",
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = "#22c55e";
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.background = "rgba(34,197,94,0.08)";
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,197,94,0.15)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.color = "rgba(240,237,232,0.7)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <span style={{ marginRight: 8, color: "#22c55e", fontSize: 12 }}>✦</span>
                  {c}
                </div>
              ))}
            </div>
          </AnimSection>

          <AnimSection delay={200}>
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, marginBottom: 28 }}>{t.vendorsTitle}</h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(6, 1fr)", gap: 12 }}>
              {VENDORS.map((v, i) => (
                <div key={i} style={{
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 14,
                  padding: "20px 12px",
                  textAlign: "center",
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: "rgba(240,237,232,0.55)",
                  cursor: "default",
                  background: "rgba(255,255,255,0.02)",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "hidden",
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = "#22c55e";
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.background = "rgba(34,197,94,0.08)";
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,197,94,0.15)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                    e.currentTarget.style.color = "rgba(240,237,232,0.55)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {v}
                </div>
              ))}
            </div>
          </AnimSection>
        </div>
      </section>

      {/* CERTS */}
      <section id="certs" style={{ padding: isMobile ? "60px 20px" : "100px 48px", background: "rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{ marginBottom: 64, textAlign: "center" }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.quality}</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700 }}>{t.certificates}</h2>
            </div>
          </AnimSection>

          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            {/* 3 CISCO в ряд */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
              gap: 20,
              marginBottom: 20,
            }}>
              {CERTS_CISCO.map((c, i) => (
                <AnimSection key={i} delay={i * 80}>
                  <div className="cert-card">
                    <div style={{ width: 56, height: 56, borderRadius: "50%", border: "2px solid #22c55e", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "#22c55e", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textAlign: "center", lineHeight: 1.2 }}>CISCO</div>
                    <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 10, color: "#22c55e" }}>{c.code}</div>
                    <div style={{ fontSize: 13, color: "rgba(240,237,232,0.55)", lineHeight: 1.6 }}>{c.desc}</div>
                  </div>
                </AnimSection>
              ))}
            </div>

            {/* 2 HUAWEI по центру */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(2, 1fr)",
              gap: 20,
              maxWidth: isMobile ? "100%" : "66%",
              margin: "0 auto",
            }}>
              {CERTS_HUAWEI.map((c, i) => (
                <AnimSection key={i} delay={i * 80}>
                  <div className="cert-card">
                    <div style={{ width: 56, height: 56, borderRadius: "50%", border: "2px solid #22c55e", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "#22c55e", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textAlign: "center", lineHeight: 1.2 }}>HUAWEI</div>
                    <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 10, color: "#22c55e" }}>{c.code}</div>
                    <div style={{ fontSize: 13, color: "rgba(240,237,232,0.55)", lineHeight: 1.6 }}>{c.desc}</div>
                  </div>
                </AnimSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" style={{ padding: isMobile ? "60px 20px" : "100px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <AnimSection>
            <div style={{
              border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: 28,
              padding: isMobile ? "36px 24px" : "72px 64px",
              background: "rgba(34,197,94,0.03)",
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 40 : 64,
              alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#22c55e", marginBottom: 14 }}>{t.contactTitle}</div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 700, lineHeight: 1.2, marginBottom: 20 }}>
                  {t.contactHeading}
                </h2>
                <p style={{ fontSize: 15, color: "rgba(240,237,232,0.6)", lineHeight: 1.7, marginBottom: 36 }}>
                  {t.contactText}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {[
                    { icon: "📞", text: "+7 (702) 672-94-98", href: "tel:+77026729498" },
                    { icon: "✉️", text: "service@telecommunity.kz", href: "mailto:service@telecommunity.kz" },
                    { icon: "📍", text: t.location, href: "#" },
                  ].map((item, i) => (
                    <a key={i} href={item.href} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, color: "rgba(240,237,232,0.7)", transition: "color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.color = "#f0ede8"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(240,237,232,0.7)"}
                    >
                      <span>{item.icon}</span>
                      <span>{item.text}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { key: "name", label: t.formName, placeholder: t.formNamePlaceholder },
                  { key: "email", label: t.formEmail, placeholder: "example@gmail.com" },
                  { key: "phone", label: t.formPhone, placeholder: "+77001234567" },
                  { key: "company", label: t.formCompany, placeholder: t.formCompanyPlaceholder },
                ].map((f) => (
                  <div key={f.key}>
                    <label style={{ fontSize: 11, color: "rgba(240,237,232,0.45)", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                      {f.label} <span style={{ color: "#22c55e" }}>*</span>
                    </label>
                    <input
                      placeholder={f.placeholder}
                      value={formData[f.key]}
                      onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{
                        width: "100%",
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${formErrors[f.key] ? "#ef4444" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: 10,
                        padding: "12px 16px",
                        color: "#f0ede8",
                        fontSize: 14,
                        outline: "none",
                      }}
                      onFocus={e => e.target.style.borderColor = formErrors[f.key] ? "#ef4444" : "rgba(34,197,94,0.5)"}
                      onBlur={e => e.target.style.borderColor = formErrors[f.key] ? "#ef4444" : "rgba(255,255,255,0.1)"}
                    />
                    {formErrors[f.key] && (
                      <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{formErrors[f.key]}</div>
                    )}
                  </div>
                ))}

                <div>
                  <label style={{ fontSize: 11, color: "rgba(240,237,232,0.45)", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                    {t.formMessage} <span style={{ color: "#22c55e" }}>*</span>
                  </label>
                  <textarea
                    placeholder={t.formMessagePlaceholder}
                    rows={3}
                    value={formData.message}
                    onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                    style={{
                      width: "100%",
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${formErrors.message ? "#ef4444" : "rgba(255,255,255,0.1)"}`,
                      borderRadius: 10,
                      padding: "12px 16px",
                      color: "#f0ede8",
                      fontSize: 14,
                      outline: "none",
                      resize: "none",
                      fontFamily: "inherit",
                    }}
                    onFocus={e => e.target.style.borderColor = formErrors.message ? "#ef4444" : "rgba(34,197,94,0.5)"}
                    onBlur={e => e.target.style.borderColor = formErrors.message ? "#ef4444" : "rgba(255,255,255,0.1)"}
                  />
                  {formErrors.message && (
                    <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{formErrors.message}</div>
                  )}
                </div>

                {formStatus === "success" && (
                  <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 10, padding: "12px 16px", fontSize: 14, color: "#22c55e" }}>
                    ✓ Заявка отправлена! Мы свяжемся с вами в течение рабочего дня.
                  </div>
                )}
                {formStatus === "error" && (
                  <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "12px 16px", fontSize: 14, color: "#ef4444" }}>
                    ✗ Ошибка отправки. Попробуйте ещё раз или напишите нам напрямую.
                  </div>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    className="btn-primary"
                    style={{ flex: 1, opacity: formStatus === "sending" ? 0.7 : 1 }}
                    onClick={handleSubmit}
                    disabled={formStatus === "sending"}
                  >
                    {formStatus === "sending" ? t.sending : t.send}
                  </button>
                  <a href="https://wa.me/77026729498" target="_blank" rel="noreferrer">
                    <button className="btn-outline" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.855L.057 23.03a.75.75 0 00.914.914l5.243-1.462A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.718 9.718 0 01-4.964-1.364l-.355-.211-3.107.866.88-3.02-.23-.371A9.718 9.718 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                      </svg>
                      WhatsApp
                    </button>
                  </a>
                  <a href="https://t.me/KazTechNet_bot" target="_blank" rel="noreferrer">
                    <button className="btn-outline" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.48 14.373l-2.95-.924c-.64-.203-.654-.64.136-.954l11.5-4.433c.537-.194 1.006.131.396.186z"/>
                      </svg>
                      Telegram
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: isMobile ? "32px 20px" : "48px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", flexWrap: "wrap", gap: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
            <rect x="4" y="4" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="22" y="4" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="4" y="22" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <rect x="22" y="22" width="14" height="14" rx="2" stroke="#22c55e" strokeWidth="2" fill="none"/>
          </svg>
          <div>
            <div style={{ fontWeight: 700, fontSize: 24, letterSpacing: "0.08em" }}>KazTechNet</div>
            <div style={{ fontSize: 10, color: "rgba(240,237,232,0.35)", letterSpacing: "0.1em" }}>{t.footerRights}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: isMobile ? 16 : 28, flexWrap: "wrap" }}>
          {NAV_LINKS.map(l => (
            <a key={l.href} href={l.href} className="nav-link" style={{ fontSize: 13 }}>{t[l.key]}</a>
          ))}
        </div>
      </footer>
    </div>
  );
}
